import { rgba } from "polished";
import React from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import styled from "styled-components";
import { theme } from "~/theme";

export const SmartObjectives: React.FC<{}> = () => {
  const [itemSelected, setItemSelected] = React.useState<number>(0);

  return (
    <>
      <div className="row mb-4">
        <div className="col-md-10 mx-auto d-flex flex-column text-center">
          <p>
            SMART objectives ensure everyone clearly understands what is being
            worked towards. As opposed to a general objective which can be
            interpreted in many different ways, SMART objectives allow you to
            confidently measure whether or not success has been achieved.
          </p>
          <p>Ensure the objectives you set cover the following:</p>
        </div>
      </div>
      <div className="row px-5 mb-5">
        {data.map(({ id, letter }) => (
          <StyledButton
            key={id}
            className="col-md-2 mx-auto"
            onClick={() => setItemSelected(id)}
          >
            <CircleDiv active={id === itemSelected}>
              <Symbol active={id === itemSelected}>{letter}</Symbol>
            </CircleDiv>
          </StyledButton>
        ))}
      </div>
      <div className="row mb-4">
        <div className="col-md-10 col-lg-8 mx-auto d-flex flex-column">
          <ItemDescription objectiveId={itemSelected} />
          <p>
            Writing SMART objectives is not a complex process; it may just take
            a little getting used to. Below are a few examples you can use as a
            basis for creating your own:
          </p>
          <ul className="bullet-fulcrum bullet-gold mb-4">
            <li>
              <p>
                Double the number of employees taking active transport to work each day over the next 6 months.
              </p>
            </li>
            <li>
              <p>
                Over the next 12 months, increase by 10% the proportion of factory staff eating fruit or vegetables as a snack each day at work.
              </p>
            </li>
            <li>
              <p>
                Decrease the proportion of employees who smoke by 30% over the
                next 6 months.
              </p>
            </li>
            <li>
              <p>
                Reduce by 50% the proportion of workshop employees who are
                drinking more than 2 standard drinks at any work event over the
                next 12 months.
              </p>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

const ItemDescription: React.FC<{ objectiveId: number }> = ({
  objectiveId,
}) => {
  const { title, content } = data[objectiveId];

  return (
    <div
      className="d-flex flex-column text-center mb-4"
      css={`
        padding: 2rem;
        background-color: ${rgba(theme.gold, 0.2)};
      `}
    >
      <SwitchTransition mode="out-in">
        <CSSTransition
          key={objectiveId}
          addEndListener={(node, done) => {
            node.addEventListener("transitionend", done, false);
          }}
          classNames="fade"
        >
          <StyledContent>
            <h1 className="h4 text-uppercase text-gold mb-3">{title}</h1>
            {content}
          </StyledContent>
        </CSSTransition>
      </SwitchTransition>
    </div>
  );
};

const StyledButton = styled.button`
  background-color: transparent;
  border: none;
  &:focus {
    outline: none;
  }
`;

const CircleDiv = styled.div<{ active: boolean }>`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  transition: transform 500ms cubic-bezier(0.34, 1.86, 0.64, 1);
  transform: ${(p) => (p.active ? "scale(1.5)" : "scale(1)")};
`;

const Symbol = styled.span<{ active: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 4em;
  color: white;
  background-color: ${(p) => (p.active ? theme.info : theme.gray800)};
  border-radius: 50%;
`;

const StyledContent = styled.div`
  transition: all 300ms;
  &.fade-enter {
    margin-left: 3rem;
    opacity: 0;
  }
  &.fade-enter.fade-enter-active {
    opacity: 1;
    margin-left: 0;
  }
  &.fade-exit {
    opacity: 1;
    margin-left: 0;
  }
  &.fade-exit.fade-exit-active {
    opacity: 0;
    margin-left: -3rem;
  }
`;

interface Data {
  id: number;
  letter: string;
  title: string;
  content: JSX.Element;
}

const data: Data[] = [
  {
    id: 0,
    letter: "S",
    title: "Specific",
    content: (
      <>
        <p>What specifically do you want to achieve?</p>
        <p>
          “Increase by 20% the proportion of employees achieving at least 30
          minutes of exercise on 6 days per week” is more specific than
          “increase the number of employees exercising”
        </p>
      </>
    ),
  },
  {
    id: 1,
    letter: "M",
    title: "Measurable",
    content: (
      <>
        <p>Can you measure if you have achieved success?</p>
        <p>
          By adding in specific figures, it will be easier to measure whether
          you have been successful e.g. You can see if there’s been a 20%
          increase in the proportion of employees achieving 30 minutes of
          exercise by surveying employees before and after your strategies are
          implemented and comparing the results.
        </p>
      </>
    ),
  },
  {
    id: 2,
    letter: "A",
    title: "Achievable",
    content: (
      <>
        <p>
          Can you realistically achieve your objective with the time and
          resources available?
        </p>
        <p>
          If you’re low on resources, including time and money, you may need to
          adjust your expectations. Objectives shouldn’t be too easy, nor too
          difficult to achieve.
        </p>
      </>
    ),
  },
  {
    id: 3,
    letter: "R",
    title: "Relevant",
    content: (
      <>
        <p>
          Does your objective reflect your organisation’s and employees’ needs?
        </p>
        <p>
          Focusing on reducing smoking rates in an organisation with no smokers would be a waste of time and money. Ensure you consult effectively so that your objectives are relevant.
        </p>
      </>
    ),
  },
  {
    id: 4,
    letter: "T",
    title: "Time bound",
    content: (
      <>
        <p>When do you want to achieve your objective by?</p>
        <p>
          Adding a completion date will help you to keep focused. Try to choose
          timelines within 6 to 12 months and remember to ensure it’s a
          realistic timeframe.
        </p>
      </>
    ),
  },
];
